import type { RowData } from "@tanstack/react-table";
import clsx from "clsx";
import type React from "react";
import { useDataTableContext } from "../context";
import type { Header } from "../types";

/** Props Interface */
interface ColumnResizerProps<T extends RowData> {
	header: Header<T, unknown>;
}

export function ColumnResizer<T extends RowData>({ header }: ColumnResizerProps<T>) {
	/** Context */
	const { tableBoxRef, tableContainerRef, table } = useDataTableContext();

	/** Computed */
	const isLast = header.column.getIsLastColumn();

	/** Guard */
	if (isLast) return null;

	/** Functions */
	function handleOnResetSize() {
		header.column.resetSize();
	}

	function handleOnMouseDown(startEvent: React.MouseEvent<HTMLDivElement>) {
		startEvent.preventDefault();
		startEvent.stopPropagation();

		const startX = startEvent.clientX;
		const startSize = header.column.getSize();
		const minSize = header.column.columnDef.minSize ?? 20;
		const maxSize = header.column.columnDef.maxSize ?? Number.MAX_SAFE_INTEGER;
		const headerId = header.id;
		const colId = header.column.id;

		const containerEl = tableContainerRef.current;
		if (!containerEl) return;

		const containerRect = containerEl.getBoundingClientRect();

		// Ghost line: position: fixed, moved via transform (compositor-only — zero layout cost per frame)
		const ghost = document.createElement("div");
		Object.assign(ghost.style, {
			position: "fixed",
			top: `${containerRect.top}px`,
			height: `${containerRect.height}px`,
			left: `${startX}px`,
			width: "2px",
			background: "var(--color-accent-500)",
			opacity: "0.75",
			zIndex: "9999",
			pointerEvents: "none",
			willChange: "transform",
		});
		document.body.appendChild(ghost);

		let lastSize = startSize;

		function onMouseMove(e: MouseEvent) {
			lastSize = Math.min(maxSize, Math.max(minSize, startSize + (e.clientX - startX)));
			// translateX is compositor-only — no layout, no style recalc for table cells
			ghost.style.transform = `translateX(${lastSize - startSize}px)`;
		}

		function onMouseUp() {
			ghost.remove();
			// One reflow for all cells — apply final size
			const el = tableBoxRef.current;
			if (el) {
				el.style.setProperty(`--header-${headerId}-size`, String(lastSize));
				el.style.setProperty(`--col-${colId}-size`, String(lastSize));
			}
			table.setColumnSizing((prev) => ({ ...prev, [colId]: lastSize }));
			document.removeEventListener("mousemove", onMouseMove);
			document.removeEventListener("mouseup", onMouseUp);
		}

		document.addEventListener("mousemove", onMouseMove);
		document.addEventListener("mouseup", onMouseUp);
	}

	/** Render */
	return (
		<div
			className={clsx(
				"top-0 right-0 absolute w-2 h-full cursor-col-resize select-none touch-none",
				"hover:bg-accent-500/20 active:bg-accent-500/50 transition-colors duration-100",
			)}
			onMouseDown={handleOnMouseDown}
			onClick={(e) => e.stopPropagation()}
			onKeyUp={() => {}}
			onDoubleClick={handleOnResetSize}
		/>
	);
}
